/**
 * Main Layout Component
 * Combines TopBar, Sidebar, and main content area
 */

import React, { useEffect, useState } from 'react';
import { APP_BAR_HEIGHT } from '../../theme/layoutConstants';
import {
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { toggleSidebar } from '../../store/uiSlice';
import { hasAccess } from '../../config/permissionResources';
import { prefetchOverview, overviewOwner } from '../../services/overviewPrefetch';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import P0AlarmBanner from '../alerts/P0AlarmBanner';
import { HelpDrawerMount } from '../help';
import InactivityManager from '../inactivity/InactivityManager';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch<AppDispatch>();
  
  const { sidebarOpen, sidebarCollapsed } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);
  const owner = overviewOwner(user);
  const canSeeOverview = Boolean(user && hasAccess(user.permissions || [], 'overview', user.email));

  // Fetch the Overview's data in the background once per signed-in session, so
  // it is ready before anyone clicks it — it takes ~30 s. Layout is the shell
  // every signed-in page renders inside, so this runs once at sign-in rather
  // than on each page. The delay lets the landing page (the Ad Dashboard) load
  // first. No extra load: those nine calls used to fire at every sign-in anyway,
  // when the Overview was the landing page.
  useEffect(() => {
    if (!owner || !canSeeOverview) return undefined;
    const t = window.setTimeout(() => {
      prefetchOverview(owner).catch(() => { /* the Overview reports its own errors when opened */ });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [owner, canSeeOverview]);
  
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      dispatch(toggleSidebar());
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <InactivityManager>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />

        {/* TopBar */}
        <TopBar onMenuClick={handleDrawerToggle} />

        {/* Sidebar */}
        <Sidebar
          open={isMobile ? mobileOpen : sidebarOpen}
          onClose={handleMobileDrawerClose}
          collapsed={!isMobile && sidebarCollapsed}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: `${APP_BAR_HEIGHT}px`, // TopBar height
            transition: theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            backgroundColor: theme.palette.background.default,
            minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* P0 Alarm Banner — shows when API health checks are down */}
          <P0AlarmBanner />
          {/* Content Area */}
          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {children}
          </Box>
        </Box>

        {/* Help Drawer — Phase 8 Prompt 2; opened from TopBar "?" button */}
        <HelpDrawerMount />
      </Box>
    </InactivityManager>
  );
};

export default Layout;